import { Center, Flex, SimpleGrid, Spinner, Text, Textarea } from '@chakra-ui/react';
import { skipToken } from '@tanstack/react-query';
import { useMemo } from 'react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';

import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { overflowScrollbarStyles } from '@duke-hq/ui/styles/overflowScrollbarStyles';
import { AssessmentOutcomeSchema } from '@duke-hq/svc-api-db/schemas/enums/AssessmentOutcome.schema';

import AssessmentSummary from '../components/AssessmentSummary';
import { AssessmentOutcomeConfirmationDialog } from './AssessmentOutcomeConfirmationDialog';
import { AssessmentOverviewAssessed } from './AssessmentOverviewAssessed';
import { OverallFeedback } from './OverallFeedback';
import { useMarkingContext } from './context/MarkingContext';

export function Overview() {
  const { assessmentSubmission, overviewState, selectSlide } = useMarkingContext();
  const { unitAssessmentVersionId } = assessmentSubmission;

  const navigableSlideIds = useMemo(
    () => new Set(assessmentSubmission.assessorSlides.map(({ slideId }) => slideId)),
    [assessmentSubmission.assessorSlides],
  );

  if (unitAssessmentVersionId == null) {
    throw new Error('unitAssessmentVersionId is required to load slides');
  }

  const [{ slides: allSlides }] = trpcAdmin.unitAssessment.getSlides.useSuspenseQuery({
    unitAssessmentVersionId,
  });

  const { data: allAssessmentSubmissions, isFetching: isLoadingHistory } =
    trpcAdmin.assessments.getAllByAssessmentEnrolmentId.useQuery(
      assessmentSubmission.unit_assessment_enrolment_id ?? skipToken,
      {
        enabled: Boolean(assessmentSubmission.unit_assessment_enrolment_id),
      },
    );

  const hasCurrentSubmissionAssessed = assessmentSubmission.assessed_at !== null;
  const {
    feedback,
    setFeedback,
    reason,
    setReason,
    selectedOutcome,
    isOverriding,
    isLoadingOutcome,
    isConfirmingOutcomeUpdate,
    closeConfirmationDialog,
    handleConfirmOutcomeUpdate,
    pendingOutcomeData,
    willCompleteCourseWhenSatisfactory,
    isCourseEnrolmentCompleted,
    isSubmitting,
  } = overviewState;

  return (
    <>
      <SimpleGrid columns={2} width="full" height="full" overflow="hidden">
        <Flex
          p={4}
          flexDirection="column"
          height="full"
          width="full"
          bg="bg.canvas"
          overflowX="auto"
          overflowY="scroll"
          css={overflowScrollbarStyles}
        >
          {isLoadingHistory ? (
            <Center w="full" h="full">
              <Spinner />
            </Center>
          ) : allAssessmentSubmissions ? (
            allAssessmentSubmissions.map((submission, index) => (
              <AssessmentSummary
                key={submission.id}
                submission={submission}
                slides={allSlides}
                revisionNumber={(allAssessmentSubmissions?.length ?? 0) - index}
                onQuestionClick={selectSlide}
                isQuestionNavigable={(questionId) => navigableSlideIds.has(questionId)}
              />
            ))
          ) : null}
        </Flex>

        <Flex
          bg="bg.subtle"
          p={4}
          flexDirection="column"
          height="full"
          width="full"
          overflowX="auto"
          overflowY="scroll"
          css={overflowScrollbarStyles}
        >
          <Flex height="full" width="full" alignItems="center" justifyContent="center">
            {isLoadingOutcome && !hasCurrentSubmissionAssessed ? (
              <Center height="full" width="full">
                <Spinner />
              </Center>
            ) : hasCurrentSubmissionAssessed ? (
              <Flex height="full" width="full" alignItems="start" flexDirection="column" gap={6}>
                <AssessmentOverviewAssessed assessmentSubmission={assessmentSubmission} />
                <OverallFeedback assessmentSubmission={assessmentSubmission} />
              </Flex>
            ) : (
              <Flex height="full" width="full" flexDirection="column" gap={6} px={3}>
                <Flex direction="column" w="full" gap={2}>
                  <Text color="fg.muted" fontSize="sm" fontWeight="medium">
                    {selectedOutcome === AssessmentOutcomeSchema.enum.SATISFACTORY
                      ? t`Feedback for satisfactory completion`
                      : t`Feedback for revision`}
                  </Text>
                  <Textarea
                    value={feedback}
                    placeholder={t`Provide assessment feedback...`}
                    size="sm"
                    onChange={setFeedback}
                    resize="vertical"
                    isDisabled={isSubmitting}
                  />
                </Flex>
                {isOverriding ? (
                  <Flex direction="column" w="full" gap={2}>
                    <Text color="fg.muted" fontSize="sm" fontWeight="medium">
                      <Trans>Reason for this override</Trans>
                    </Text>
                    <Textarea
                      value={reason}
                      placeholder={t`Explain why you are overriding the system's assessment outcome...`}
                      size="sm"
                      bg="bg.warning-subtle"
                      onChange={setReason}
                      resize="vertical"
                      isDisabled={isSubmitting}
                    />
                  </Flex>
                ) : null}
              </Flex>
            )}
          </Flex>
        </Flex>
      </SimpleGrid>

      <AssessmentOutcomeConfirmationDialog
        isOpen={isConfirmingOutcomeUpdate}
        onClose={closeConfirmationDialog}
        onConfirm={handleConfirmOutcomeUpdate}
        pendingOutcome={pendingOutcomeData}
        isCourseEnrolmentCompleted={isCourseEnrolmentCompleted}
        willCompleteCourseWhenSatisfactory={willCompleteCourseWhenSatisfactory}
        isLoading={isSubmitting}
      />
    </>
  );
}
